// Per-button settings marks — what the little icons on each relay tile mean.
//
// Three independent "this button has something configured" signals, all from
// button settings:
//
//   schedule  a schedule fires LATER TODAY        -> clock icon + the time
//   twoWay    the button is in a two-way pair     -> router icon
//   autoOff   it switches itself off after N min  -> timer icon + "3m"
//
// COST: one new request per device. The schedule rows are already fetched to
// draw the existing clock badge, and OnTimeAfterOn already rides along in the
// getOutputs() rows the screens fetch for button names — so only the two-way
// lookup is new, and that is a single call covering every button on the device.

import { getSchedules, getTwoWayMappings } from '../api/api';

// ── schedules ──────────────────────────────────────────────────────────────
// A schedule row carries BOTH times, either of which may be unset:
//   Weekly            "1,0,1,0,1,0,0"  day flags, Sun..Sat
//   FunctionOnTimer   minutes from midnight to switch ON   (0 = unset)
//   FunctionOffTimer  minutes from midnight to switch OFF  (0 = unset)
// so one row can produce an On event AND an Off event.
//
// There are NO dates in this table — a schedule repeats on the chosen weekdays
// indefinitely. "Today" therefore means "today's weekday flag is set", nothing
// more.

const runsToday = (weekly, weekday) => {
  const flags = String(weekly || '').split(',');
  return flags[weekday] === '1';
};

// -> [{ minutes, action }] for one row, today only.
function eventsOf(row, weekday) {
  if (!runsToday(row.Weekly, weekday)) return [];
  const out = [];
  const on = Number(row.FunctionOnTimer) || 0;
  const off = Number(row.FunctionOffTimer) || 0;
  if (on > 0) out.push({ minutes: on, action: 'On' });
  if (off > 0) out.push({ minutes: off, action: 'Off' });
  return out;
}

export function formatTime(minutes) {
  const h24 = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  const suffix = h24 >= 12 ? 'pm' : 'am';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
}

// The next schedule event still to come today for one output, or null.
// `nowMinutes` is passed in rather than read here so the whole screen evaluates
// against a single instant, and so a tick can re-filter without refetching.
export function nextPending(rows, weekday, nowMinutes) {
  const upcoming = (rows || [])
    .flatMap((r) => eventsOf(r, weekday))
    .filter((e) => e.minutes > nowMinutes)
    .sort((a, b) => a.minutes - b.minutes);
  return upcoming.length ? upcoming[0] : null;
}

// ── auto-off ───────────────────────────────────────────────────────────────
// OutputDetails.OnTimeAfterOn — minutes to switch off after a key press.
// (Siblings OnTimeAfterAllOn / OnTimeAfterAllOff are the equivalents after
// All On / All Off; not surfaced as marks, but they are on the same row.)
/**
 * The row for one output, matched on its Output NUMBER.
 *
 * ⚠️ NOT `rows[output - 1]`. GetOutputDetailbyUniqueNumber returns rows in
 * whatever order the query produces, and a device whose outputs have been
 * edited can come back out of order or with a gap. Position-matching then
 * quietly attaches one button's name and auto-off mark to a different button —
 * which looks like the setting having moved, not like a bug.
 */
export function rowForOutput(rows, output) {
  const n = Number(output);
  return (rows || []).find((r) => Number(r.Output) === n) || null;
}

export function autoOffMinutes(outputRow) {
  const v = Number(outputRow && outputRow.OnTimeAfterOn) || 0;
  return v > 0 ? v : 0;
}

export function formatDuration(min) {
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h}h${m}` : `${h}h`;
}

// ── All On / All Off behaviour ─────────────────────────────────────────────
// What a button does when the device-wide All On / All Off bars are pressed.
//
// ⚠️ BOTH CHOICES LIVE IN ONE COLUMN, as a two-digit code:
//
//     on_all_on_status = (allOn * 10) + allOff
//
// (buttonSettingsFunctions.dart:505; the firmware reads the same pair as the
// two digits of register 48 + output.) So `on_all_on_status = 1` does NOT mean
// "participates in All On" — it decodes to allOn=0, allOff=1, i.e. "acts
// normally on All On, IGNORES All Off". Reading the column as a boolean gets
// the meaning exactly backwards.
//
// The sibling columns off_all_off_status / on_all_off_status / off_all_on_status
// exist in the table but the Dart never reads them; they are legacy.
export const ALL_ON_LABELS = ['Action On', 'No Action', 'Schedule Time', 'Reverse'];
export const ALL_OFF_LABELS = ['Action Off', 'No Action', 'Schedule Time', 'Reverse'];

// Short forms for the tile badge, indexed the same way. Index 0 is the default
// and never shown.
const SHORT = ['', 'no', 'sch', 'rev'];

const clamp03 = (n) => Math.min(3, Math.max(0, n));

/**
 * -> { allOn, allOff, isDefault, label, description }
 *
 * `isDefault` means the button behaves normally on both bars, which is the
 * overwhelming majority — a mark is only worth drawing when it does not.
 */
export function allOnOffBehaviour(outputRow) {
  const status = Number(outputRow && outputRow.on_all_on_status) || 0;
  const allOn = clamp03(Math.floor(status / 10));
  const allOff = clamp03(status % 10);
  const isDefault = allOn === 0 && allOff === 0;

  const parts = [];
  if (allOn !== 0) parts.push(`on:${SHORT[allOn]}`);
  if (allOff !== 0) parts.push(`off:${SHORT[allOff]}`);

  return {
    allOn,
    allOff,
    isDefault,
    label: parts.join(' '),
    description: `All On: ${ALL_ON_LABELS[allOn]} · All Off: ${ALL_OFF_LABELS[allOff]}`,
  };
}

// ── Button State — what the output does when power comes back ──────────────
// OutputDetails.PowerOnAction. The backend's power-on jobs act on the column
// directly, so nothing is published when it changes.
//
// Option 2 is not a general "restart": it exists because an output may be
// feeding a WI-FI ROUTER. On power restore the switch waits a minute and then
// cycles, so the router comes up cleanly — and a device wired that way behaves
// differently from every other button on the same panel, which is worth showing.
export const BUTTON_STATE_LABELS = [
  'Remember Last State',
  'Do Not Remember Last State',
  'Restart After One Minute (For Router)',
];

export const BUTTON_STATE_REMEMBER = 0;
export const BUTTON_STATE_FORGET = 1;
export const BUTTON_STATE_ROUTER = 2;

// Short forms for the tile badge. Index 0 is the default and never shown.
const STATE_SHORT = ['', 'no recall', 'router'];

/** -> { value, isDefault, label, description } */
export function buttonState(outputRow) {
  const raw = Number(outputRow && outputRow.PowerOnAction) || 0;
  const value = Math.min(2, Math.max(0, raw));
  return {
    value,
    isDefault: value === BUTTON_STATE_REMEMBER,
    label: STATE_SHORT[value],
    description: BUTTON_STATE_LABELS[value],
  };
}

// ── two-way ────────────────────────────────────────────────────────────────
// A mapping names both halves. This device may be either half, so both sides
// are checked and the OTHER half is returned as the partner.
//
// `FristButtonNumber` is the backend's spelling. Do not "fix" it.
export function twoWayPartner(mappings, uniqueNumber, output) {
  const uno = String(uniqueNumber);
  const out = Number(output);
  for (const m of mappings || []) {
    if (String(m.FirstUniqueNumber) === uno && Number(m.FristButtonNumber) === out) {
      return { uniqueNumber: m.SecondUniqueNumber, output: Number(m.SecondButtonNumber) };
    }
    if (String(m.SecondUniqueNumber) === uno && Number(m.SecondButtonNumber) === out) {
      return { uniqueNumber: m.FirstUniqueNumber, output: Number(m.FristButtonNumber) };
    }
  }
  return null;
}

// ── the one call a screen makes ────────────────────────────────────────────
// Returns { schedulesByOutput, mappings } for a device. Outputs are 1-based.
//
// Failures degrade to "no marks" rather than breaking the screen: an indicator
// is a nicety, and a device must stay controllable when the settings lookups
// are unavailable (they are not in the offline cache — the scheduler has no
// sync endpoint).
export async function loadButtonSettings(uniqueNumber, outputCount) {
  const outputs = Array.from({ length: outputCount }, (_, i) => i + 1);

  const [scheduleResults, mappingRes] = await Promise.all([
    Promise.all(outputs.map((o) => getSchedules(uniqueNumber, o)
      .then((res) => (Array.isArray(res.data) ? res.data : []))
      .catch(() => []))),
    getTwoWayMappings(uniqueNumber)
      .then((res) => (Array.isArray(res.data) ? res.data : []))
      .catch(() => []),
  ]);

  const schedulesByOutput = {};
  outputs.forEach((o, i) => { schedulesByOutput[o] = scheduleResults[i]; });

  return { schedulesByOutput, mappings: mappingRes };
}

// Everything a tile needs for one output, given already-loaded data.
export function marksFor({
  output, schedulesByOutput, mappings, outputRow, uniqueNumber, weekday, nowMinutes,
}) {
  const rows = (schedulesByOutput && schedulesByOutput[output]) || [];
  const pending = nextPending(rows, weekday, nowMinutes);
  const autoOff = autoOffMinutes(outputRow);
  const partner = twoWayPartner(mappings, uniqueNumber, output);
  const allBars = allOnOffBehaviour(outputRow);
  const state = buttonState(outputRow);
  return {
    // Any schedule at all (even past/other days) — keeps the existing badge
    // behaviour, which marked a button as "scheduled" regardless of timing.
    hasSchedule: rows.length > 0,
    pending,                                   // { minutes, action } | null
    pendingLabel: pending ? formatTime(pending.minutes) : '',
    autoOff,                                   // minutes, 0 = none
    autoOffLabel: autoOff ? formatDuration(autoOff) : '',
    partner,                                   // { uniqueNumber, output } | null
    // Only carried when the button does NOT behave normally on the All bars —
    // the default is nearly universal and marking it would mark everything.
    allBars: allBars.isDefault ? null : allBars,
    // Same rule: only when it is not "Remember Last State".
    buttonState: state.isDefault ? null : state,
  };
}
